import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useFeatureFlags } from '../../context/FeatureFlagContext';
import { FaBars, FaTimes, FaChevronDown, FaBolt } from 'react-icons/fa';
import AppStoreBadges from '../AppPromo/AppStoreBadges';
import './NavBar.css';

const NavBar = () => {
  const { isAuthenticated, logout, userRole } = useAuth();
  const { isFeatureEnabled } = useFeatureFlags();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useRef(null);

  // Close everything on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
        setOpenDropdown(null);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleDropdownClick = (name) => {
    setOpenDropdown(prev => prev === name ? null : name);
  };

  const isActive = (path) => location.pathname === path;
  const isDropdownActive = (paths) => paths.some(p => location.pathname === p);

  return (
    <nav className="navbar" ref={navRef}>
      <div className="nav-container">
        <Link to="/" className="nav-logo">
          <img src="/icons/icon.png" alt="Tocxi" className="nav-logo-img" />
          <span className="nav-logo-text">TOCXI</span>
        </Link>

        <div className={`nav-menu ${isMobileMenuOpen ? 'active' : ''}`}>
          {isAuthenticated ? (
            <>
              {userRole === 'USER' && (
                <>
                  <Link to="/bookings" className={`nav-link ${isActive('/bookings') ? 'nav-active' : ''}`}>
                    Ride
                  </Link>

                  <Link to="/wallet" className={`nav-link ${isActive('/wallet') ? 'nav-active' : ''}`}>
                    Wallet
                  </Link>

                  {/* Services Dropdown */}
                  <div className="nav-dropdown">
                    <button
                      className={`nav-link nav-dropdown-toggle ${isDropdownActive(['/parcels', '/parking', '/bus', '/commute', '/energy', '/operator-onboarding']) ? 'nav-active' : ''}`}
                      onClick={() => handleDropdownClick('services')}
                      aria-haspopup="true"
                      aria-expanded={openDropdown === 'services'}
                    >
                      Services <FaChevronDown className={`nav-chevron ${openDropdown === 'services' ? 'nav-chevron-open' : ''}`} />
                    </button>
                    <div className={`nav-dropdown-menu ${openDropdown === 'services' ? 'open' : ''}`}>
                      <Link to="/parcels" className={`nav-dropdown-item ${isActive('/parcels') ? 'active' : ''}`}>
                        Parcel Delivery
                      </Link>
                      {isFeatureEnabled('parking') && (
                      <Link to="/parking" className={`nav-dropdown-item ${isActive('/parking') ? 'active' : ''}`}>
                        Parking
                      </Link>
                      )}
                      <Link to="/bus" className={`nav-dropdown-item ${isActive('/bus') ? 'active' : ''}`}>
                        Bus Tracking
                      </Link>
                      {isFeatureEnabled('commuteBuddy') && (
                      <Link to="/commute" className={`nav-dropdown-item ${isActive('/commute') ? 'active' : ''}`}>
                        Commute Buddy
                      </Link>
                      )}
                      <Link to="/energy" className={`nav-dropdown-item ${isActive('/energy') ? 'active' : ''}`}>
                        Fuel & EV Charge
                      </Link>
                      <Link to="/operator-onboarding" className={`nav-dropdown-item ${isActive('/operator-onboarding') ? 'active' : ''}`}>
                        Partner with Us
                      </Link>
                    </div>
                  </div>

                  {/* Travel Dropdown */}
                  {(isFeatureEnabled('outstation') || isFeatureEnabled('tours')) && (
                  <div className="nav-dropdown">
                    <button
                      className={`nav-link nav-dropdown-toggle ${isDropdownActive(['/outstation', '/tours']) ? 'nav-active' : ''}`}
                      onClick={() => handleDropdownClick('travel')}
                      aria-haspopup="true"
                      aria-expanded={openDropdown === 'travel'}
                    >
                      Travel <FaChevronDown className={`nav-chevron ${openDropdown === 'travel' ? 'nav-chevron-open' : ''}`} />
                    </button>
                    <div className={`nav-dropdown-menu ${openDropdown === 'travel' ? 'open' : ''}`}>
                      {isFeatureEnabled('outstation') && (
                      <Link to="/outstation" className={`nav-dropdown-item ${isActive('/outstation') ? 'active' : ''}`}>
                        Outstation Rides
                      </Link>
                      )}
                      {isFeatureEnabled('tours') && (
                      <Link to="/tours" className={`nav-dropdown-item ${isActive('/tours') ? 'active' : ''}`}>
                        Tour Packages
                      </Link>
                      )}
                    </div>
                  </div>
                  )}

                  {/* Rewards Dropdown */}
                  <div className="nav-dropdown">
                    <button
                      className={`nav-link nav-dropdown-toggle ${isDropdownActive(['/rewards', '/subscriptions']) ? 'nav-active' : ''}`}
                      onClick={() => handleDropdownClick('rewards')}
                      aria-haspopup="true"
                      aria-expanded={openDropdown === 'rewards'}
                    >
                      Rewards <FaChevronDown className={`nav-chevron ${openDropdown === 'rewards' ? 'nav-chevron-open' : ''}`} />
                    </button>
                    <div className={`nav-dropdown-menu ${openDropdown === 'rewards' ? 'open' : ''}`}>
                      <Link to="/rewards" className={`nav-dropdown-item ${isActive('/rewards') ? 'active' : ''}`}>
                        Referrals & Prizes
                      </Link>
                      <Link to="/subscriptions" className={`nav-dropdown-item ${isActive('/subscriptions') ? 'active' : ''}`}>
                        Subscriptions
                      </Link>
                    </div>
                  </div>

                  {/* Account Dropdown */}
                  <div className="nav-dropdown">
                    <button
                      className={`nav-link nav-dropdown-toggle ${isDropdownActive(['/settings', '/safety', '/dashboard']) ? 'nav-active' : ''}`}
                      onClick={() => handleDropdownClick('account')}
                      aria-haspopup="true"
                      aria-expanded={openDropdown === 'account'}
                    >
                      Account <FaChevronDown className={`nav-chevron ${openDropdown === 'account' ? 'nav-chevron-open' : ''}`} />
                    </button>
                    <div className={`nav-dropdown-menu ${openDropdown === 'account' ? 'open' : ''}`}>
                      <Link to="/dashboard" className={`nav-dropdown-item ${isActive('/dashboard') ? 'active' : ''}`}>
                        Dashboard
                      </Link>
                      <Link to="/settings" className={`nav-dropdown-item ${isActive('/settings') ? 'active' : ''}`}>
                        Settings
                      </Link>
                      <Link to="/safety" className={`nav-dropdown-item ${isActive('/safety') ? 'active' : ''}`}>
                        Safety Center
                      </Link>
                    </div>
                  </div>
                </>
              )}
              {userRole === 'DRIVER' && (
                <>
                  <Link to="/drivers" className={`nav-link ${isActive('/drivers') ? 'nav-active' : ''}`}>Driver Panel</Link>
                  <Link to="/dashboard" className={`nav-link ${isActive('/dashboard') ? 'nav-active' : ''}`}>Dashboard</Link>

                  {/* Account dropdown for drivers */}
                  <div className={`nav-dropdown ${openDropdown === 'driver-account' ? 'is-open' : ''}`}>
                    <button
                      className={`nav-link nav-dropdown-toggle ${['/settings', '/safety', '/wallet'].includes(location.pathname) && openDropdown !== 'driver-account' ? 'nav-active' : ''}`}
                      onClick={() => handleDropdownClick('driver-account')}
                      aria-haspopup="true"
                      aria-expanded={openDropdown === 'driver-account'}
                    >
                      Account <FaChevronDown className={`nav-chevron ${openDropdown === 'driver-account' ? 'nav-chevron-open' : ''}`} />
                    </button>
                    <div className={`nav-dropdown-menu ${openDropdown === 'driver-account' ? 'open' : ''}`}>
                      <Link to="/settings" className={`nav-dropdown-item ${isActive('/settings') ? 'active' : ''}`}>
                        Settings
                      </Link>
                      <Link to="/safety" className={`nav-dropdown-item ${isActive('/safety') ? 'active' : ''}`}>
                        Safety Center
                      </Link>
                      <Link to="/wallet" className={`nav-dropdown-item ${isActive('/wallet') ? 'active' : ''}`}>
                        Wallet
                      </Link>
                    </div>
                  </div>
                </>
              )}
              <button className="logout-btn" onClick={handleLogout}>Logout</button>
            </>
          ) : (
            <>
              <Link to="/about" className={`nav-link ${isActive('/about') ? 'nav-active' : ''}`}>About</Link>
              <Link to="/support" className={`nav-link ${isActive('/support') ? 'nav-active' : ''}`}>Support</Link>
              <Link to="/login" className="nav-link nav-link-cta">Sign In</Link>
            </>
          )}

          {/* Persistent install link — visible in every state */}
          <div className="nav-dropdown nav-dropdown--app">
            <button
              className="nav-link nav-dropdown-toggle nav-link-getapp"
              onClick={() => handleDropdownClick('getapp')}
              aria-haspopup="true"
              aria-expanded={openDropdown === 'getapp'}
            >
              Get App <FaChevronDown className={`nav-chevron ${openDropdown === 'getapp' ? 'nav-chevron-open' : ''}`} />
            </button>
            <div className={`nav-dropdown-menu nav-dropdown-menu--wide ${openDropdown === 'getapp' ? 'open' : ''}`}>
              <div className="nav-getapp-pop">
                <p className="nav-getapp-headline">Get the Tocxi rider app</p>
                <AppStoreBadges align="center" size="sm" />
              </div>
            </div>
          </div>
        </div>

        <div
          className="mobile-menu-icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          role="button"
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsMobileMenuOpen(!isMobileMenuOpen); }}
        >
          {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
        </div>
      </div>
    </nav>
  );
};

export default NavBar;
